import { Listbox } from '@headlessui/react';
import { PROMOTION_TYPES } from '@weft/types';
import type { PromotionConfigurationInput, PromotionType } from '@weft/types';
import type { CreditPromotionConfigurationInput, FreeTrialPromotionConfigurationInput } from '@weft/types';
import { entries, isNotNullOrUndefined, isEmptyString } from '@weft/utils';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { ErrorLabel } from '../../../../../components/design/ErrorLabel';
import { Input } from '../../../../../components/design/Input';
import { ArrowIcon } from '../../../../../components/icons/ArrowIcon';
import { ARROW_ICON_DIRECTIONS } from '../../../../../components/icons/ArrowIcon/types/arrow-icon-direction';
import { useFormErrorNotificationContext } from '../../../../../contexts/form-error-notification-context/form-error-notification-context';
import { PROMOTION_SETTINGS } from '../../create-plan-page-consts/promotions-settings';
import type { NewPlanForm } from '../CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../CreatePlanForm/create-plan-form-types/plan-input-form';
import type { CreatePlanTab } from '../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import { CREATE_PLAN_TABS } from '../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';

export type PromotionTypeDropdownProps = {
  promotionIndex: number,
  promotionOptions: Record<string, [string, boolean]>,
};

export const PromotionTypeDropdown: React.FC<PromotionTypeDropdownProps> = ({ promotionIndex, promotionOptions }) => {
  const { registerErrorField } = useFormErrorNotificationContext<PlanInputForm, CreatePlanTab>();
  const { getFieldState, resetField, register, getValues, setValue } = useFormContext<PlanInputForm, NewPlanForm>();

  const { ref: promotionTypeRef, ...promotionTypeRegister } = register(`promotions.${promotionIndex}.promotionModel.promotionType`);

  const promotionType: PromotionType = getValues(`promotions.${promotionIndex}.promotionModel.promotionType`) ?? PROMOTION_TYPES.FreeTrial;
  const configAmountRegister = register(`promotions.${promotionIndex}.promotionModel.promotionConfiguration.${(PROMOTION_SETTINGS[promotionType].units)}`, {
    setValueAs: (amount: string) => isEmptyString(amount) ? null : parseFloat(amount),
  });

  const promotionConfig = getValues(`promotions.${promotionIndex}.promotionModel.promotionConfiguration`);
  const promotionModelError = getFieldState(configAmountRegister.name).error;

  registerErrorField(promotionTypeRegister.name, CREATE_PLAN_TABS.promotions);
  registerErrorField(configAmountRegister.name, CREATE_PLAN_TABS.promotions);
    
  return (
    <div>
      <div className="text-label mb-[7px] text-sm">Promotion type</div>
      <div className={twMerge('h-12.5 flex items-center', promotionModelError ? 'border border-input-error rounded-xl' : '')}>
        <Listbox
          as="div"
          {...promotionTypeRegister}
          className="border-gray bg-input relative flex h-full w-full flex-1 items-center justify-center rounded-l-xl border-r px-4"
          value={promotionType}
          onChange={(promotionTypeValue) => {
            resetField(`promotions.${promotionIndex}.promotionModel.promotionConfiguration`, {
              defaultValue: getDefaultPromotionConfig(promotionTypeValue),
            });
            setValue(`promotions.${promotionIndex}.promotionModel.promotionType`, promotionTypeValue);
          }}
        >
          <Listbox.Button>
            <div className="flex items-center justify-between gap-2">
              <div className="text-placeholder whitespace-nowrap text-base">{promotionOptions[getValues(`promotions.${promotionIndex}.promotionModel.promotionType`)]}</div>
              <ArrowIcon
                className='text-placeholder'
                direction={ARROW_ICON_DIRECTIONS.DOWN}
              />
            </div>
          </Listbox.Button>
          <Listbox.Options className="absolute top-[calc(100%+0.5rem)] w-full bg-white text-center">
            {entries(promotionOptions).map(([value, [displayValue, disabled]]) => (
              <Listbox.Option
                key={value.toString()}
                value={value}
                disabled={disabled}
                className={disabled ? 'text-disabled' : ''}
              >
                <div className="hover:bg-input cursor-pointer p-2 shadow-sm">{displayValue}</div>
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Listbox>
        <div className="flex w-full flex-1">
          <Input
            {...configAmountRegister}
            defaultValue={getPromotionConfigDefaultValue(promotionConfig, promotionType) ?? ''}
            type='number'
            className="rounded-l-none rounded-r-xl"
            placeholder={PROMOTION_SETTINGS[promotionType].placeholder ?? ''}
            prefix={PROMOTION_SETTINGS[promotionType].prefix}
            autoComplete='off'
            debounce={false}
          />
        </div>
      </div>
      { isNotNullOrUndefined(promotionModelError) &&
            (<ErrorLabel errorMessage={getFieldState(configAmountRegister.name)?.error?.message ?? ''}/>)
      }
    </div>
  );
};

function getPromotionConfigDefaultValue(promotionConfig: PromotionConfigurationInput, promotionType: PromotionType) {
  if (isTrialPromotionConfiguration(promotionType, promotionConfig)) {
    return promotionConfig.daysFree;
  }

  if (isCreditPromotionConfiguration(promotionType, promotionConfig)) {
    return promotionConfig.creditAmount;
  }

  if (isPercentagePromotionConfiguration(promotionType, promotionConfig)) {
    return promotionConfig.percentage;
  }

  throw new Error(`Invalid promotion type ${promotionType}`);
}

function getDefaultPromotionConfig(promotionType: PromotionType) {
  switch (promotionType) {
    case PROMOTION_TYPES.FreeTrial:
      return {
        daysFree: null,
      };
    case PROMOTION_TYPES.Credit:
      return {
        creditAmount: null,
      };
    case PROMOTION_TYPES.Percentage:
      return {
        percentage: null,
      };
    default:
      throw new Error(`Invalid promotion type ${promotionType}`);
  }
}

function isTrialPromotionConfiguration(promotionType: PromotionType, promotionConfiguration: PromotionConfigurationInput): promotionConfiguration is FreeTrialPromotionConfigurationInput {
  return promotionType === PROMOTION_TYPES.FreeTrial;
}

function isCreditPromotionConfiguration(promotionType: PromotionType, promotionConfiguration: PromotionConfigurationInput): promotionConfiguration is CreditPromotionConfigurationInput {
  return promotionType === PROMOTION_TYPES.Credit;
}

function isPercentagePromotionConfiguration(promotionType: PromotionType, promotionConfiguration: PromotionConfigurationInput): promotionConfiguration is CreditPromotionConfigurationInput {
  return promotionType === PROMOTION_TYPES.Percentage;
}
